import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usuariosAPI, getImageUrl } from '../services/api';
import { supabase } from '../services/supabaseClient';
import { useCerrarMenuAlClickFuera } from '../hooks/useCerrarMenuAlClickFuera';
import './Dashboard.css';

const Dashboard = () => {
  const [usuario, setUsuario] = useState(null);
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreTemp, setNombreTemp] = useState('');
  const [menuAbierto, setMenuAbierto] = useState(false);
  const navigate = useNavigate();
  const { logout, esAdmin } = useAuth();

  useCerrarMenuAlClickFuera(menuAbierto, setMenuAbierto);

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        cargarDatos();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const cargarDatos = async () => {
    try {
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      if (userId) {
        const respuesta = await usuariosAPI.obtenerPorId(userId);
        const usuarioActual = respuesta.data;
        setUsuario(usuarioActual);
        setNombreTemp(usuarioActual.nombre);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  const guardarNombre = async () => {
    if (nombreTemp.trim() && usuario) {
      try {
        await usuariosAPI.actualizarParcial(usuario.id, { nombre: nombreTemp });
        setUsuario({ ...usuario, nombre: nombreTemp });
        setEditandoNombre(false);
      } catch (error) {
        console.error('Error al actualizar nombre:', error);
      }
    }
  };

  const iniciarEvaluacion = async () => {
    if (usuario) {
      try {
        const respuesta = await usuariosAPI.obtenerEvaluacionInconclusa(usuario.id);
        if (respuesta.data.evaluacion) {
          if (window.confirm('Tienes una evaluación inconclusa. ¿Deseas continuar con ella?')) {
            navigate(`/evaluacion/${usuario.id}`);
          }
        } else {
          navigate(`/evaluacion/${usuario.id}`);
        }
      } catch (error) {
        console.error('Error al verificar evaluación inconclusa:', error);
        navigate(`/evaluacion/${usuario.id}`);
      }
    }
  };

  const verHistorial = () => {
    navigate('/historial');
  };

  const cambiarImagen = async (event) => {
    const file = event.target.files[0];
    if (file && usuario) {
      try {
        const path = `${usuario.id}/${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(path, file, { upsert: true });
        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
        await usuariosAPI.actualizarParcial(usuario.id, { imagen: publicUrl });
        cargarDatos();
      } catch (error) {
        console.error('Error al actualizar imagen:', error);
      }
    }
  };

  return (
    <div className="dashboard">
      <button className="menu-toggle" onClick={() => setMenuAbierto(!menuAbierto)}>
        ☰
      </button>

      <aside className={`sidebar ${menuAbierto ? 'abierto' : ''}`}>
        <div className="user-profile">
          <div className="avatar-container">
            <div className="avatar-circle" style={{ backgroundImage: usuario?.imagen ? `url(${getImageUrl(usuario.imagen)})` : 'none' }}>
              {!usuario?.imagen && <span className="avatar-placeholder">📷</span>}
            </div>
            <label htmlFor="upload-avatar" className="avatar-upload-btn">
              Cambiar foto
            </label>
            <input
              id="upload-avatar"
              type="file"
              accept="image/*"
              onChange={cambiarImagen}
              style={{ display: 'none' }}
            />
          </div>
          
          <div className="user-name-edit">
            {editandoNombre ? (
              <input
                type="text"
                value={nombreTemp}
                onChange={(e) => setNombreTemp(e.target.value)}
                onBlur={guardarNombre}
                onKeyPress={(e) => e.key === 'Enter' && guardarNombre()}
                autoFocus
                className="name-input"
              />
            ) : (
              <div className="name-display">
                <h2>{usuario?.nombre || 'Usuario'}</h2>
                <button 
                  className="edit-btn"
                  onClick={() => setEditandoNombre(true)}
                  aria-label="Editar nombre"
                  style={{ position: 'absolute', right: 0 }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="user-stats">
            <p>Nivel de liderazgo: {usuario?.nivel_liderazgo || 'Sin evaluar'}</p>
            <p>Puntuación: {usuario?.puntuacion ? Math.round(usuario.puntuacion) : '0'}</p>
          </div>
        </div>

        <div className="sidebar-buttons">
          <button className="btn-sidebar" onClick={iniciarEvaluacion}>
            Iniciar evaluación
          </button>
          <button className="btn-sidebar" onClick={verHistorial}>
            Historial de evaluaciones
          </button>
          {esAdmin && (
            <button className="btn-sidebar" onClick={() => navigate('/admin')}>
              Panel admin
            </button>
          )}
        </div>

        <div className="logout-section">
          <div className="logout-text" onClick={() => {
            logout();
            navigate('/login');
          }}>
            Cerrar sesión
          </div>
          <button className="home-btn" onClick={() => navigate('/')} aria-label="Inicio">
            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '32px', height: '32px' }}>
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1 className="welcome-title">Bienvenido</h1>
          <p className="welcome-subtitle">{usuario?.nombre || 'Nombre Usuario'}</p>
          <button className="btn-primary" onClick={iniciarEvaluacion}>
            Iniciar evaluación
          </button>
          <div className="history-section" onClick={verHistorial} role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && verHistorial()}>
            <h2 className="history-title">Historial de evaluaciones</h2>
            <div className="chart-container">
              <div className="chart-bar" style={{ height: '47%' }}></div>
              <div className="chart-bar" style={{ height: '64%' }}></div>
              <div className="chart-bar" style={{ height: '81%' }}></div>
              <div className="chart-bar" style={{ height: '40%' }}></div>
              <div className="chart-bar" style={{ height: '100%' }}></div>
              <div className="chart-bar" style={{ height: '60%' }}></div>
              <div className="chart-bar" style={{ height: '46%' }}></div>
            </div>
            <button className="ver-mas-btn" onClick={(e) => { e.stopPropagation(); verHistorial(); }}>
              Ver más
            </button>
          </div>
        </div>
      </main>

      <aside className="right-sidebar">
        <div className="cta-box">
          <p className="cta-question" style={{ textAlign: 'right' }}>
            ¿Quieres entender el resultado de tu fórmula y tu lugar en el ranking?
          </p>
          <hr className="cta-divider" />
          <p className="cta-text" style={{ textAlign: 'right' }}>
            El libro lo explica y te dice cómo potenciar tu liderazgo
          </p>
        </div>

        <div className="book-section">
          <div className="book-cover">
            <img src="/dashboard-recorte.avif" alt="La Fórmula del Liderazgo" />
          </div>
          <button className="btn-book" onClick={() => window.open('https://rodrigovillanuevallanos.com/libro', '_blank', 'noopener,noreferrer')}>Más del libro aquí</button>
        </div>
      </aside>
    </div>
  );
};

export default Dashboard;

